import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectUpdateMyDocumentsListParentFolder,
  selectUpdateMyDocumentsListSubjectId,
  setUpdateMyDocumentsList,
} from 'reducers/misc';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { Spinner } from 'components/spinner/Spinner';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { endpoints } from 'endpoints.config';
import { ErrorM } from 'components/formComponents/ErrorM';
import instance, { useFetch } from 'api';
import Chevron from '../../images/Icon_Chevron_Down.png';
import { CreateFolder } from './CreateFolder';
import { is } from 'date-fns/locale';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedId: number;
  isAllFiles: boolean;
};

export const MoveFilesAndFolders: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  selectedId,
  isAllFiles,
}) => {
  const { parentFoldersId, parentFoldersName } = useSelector(
    selectUpdateMyDocumentsListParentFolder
  );
  const dispatch = useDispatch();
  const formikRef = React.useRef<FormikProps<any>>(null);

  const params = isAllFiles
    ? { documentsId: selectedId }
    : { foldersId: selectedId };
  const url = isAllFiles
    ? endpoints.myDocuments.moveFile
    : endpoints.myDocuments.moveFolder;

  const submitForm = async (values: any, formikProps: FormikHelpers<any>) => {
    try {
      const payload = {
        ...params,
        parentFoldersId: values.selectedFolder.id,
      };
      const result = await instance.post(url, payload);

      if (result.data && result.data.status === '1') {
        dispatch(setUpdateMyDocumentsList(true));
        togglePanelOpen(false);
      }
      if (result.data && result.data.status === '0') {
        if (result.data.errors && result.data.errors.length > 0) {
          formikProps.setFieldError(
            'genericError',
            result.data.errors[0].messageCode
          );
        } else {
          formikProps.setFieldError('genericError', 'An error occurred');
        }
      }
    } catch (error) {
      formikProps.setFieldError('genericError', 'An error occurred');
    } finally {
      formikProps.setSubmitting(false);
    }
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle={`Move ${isAllFiles ? 'file' : 'folder'}`}
      position="right"
      setTop={true}
      noBackground={true}
      noHeader={true}
      noContentContainer={true}
      givenClass="DocumentHandlePage"
      noFooter={true}
      handleClose={() => togglePanelOpen(false)}
      handleSecondaryBtn={() => togglePanelOpen(false)}
      width={560}
      isSubmitting={formikRef.current?.isSubmitting}
      noOverflowToggle={true}
    >
      <Formik
        innerRef={formikRef}
        initialValues={{
          idToMove: selectedId,
          selectedFolder: {
            id: isAllFiles ? parentFoldersId : selectedId,
            name: parentFoldersName,
          },
        }}
        onSubmit={submitForm}
        enableReinitialize
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Form>
            <div className="WidthContent">
              <span
                className={'BtnBack ' + (isSubmitting ? 'Inactive' : '')}
                onClick={() => {
                  if (!isSubmitting) {
                    dispatch(setUpdateMyDocumentsList(true));
                    togglePanelOpen(false);
                  }
                }}
              >
                <span className="BB_Icon"></span>
                <span className="BB_Txt">Back</span>
              </span>

              <div className="DHPageContent">
                <h2>{`Move ${isAllFiles ? 'file' : 'folder'}`}</h2>
                <FolderTree
                  parentFoldersId={parentFoldersId}
                  selectedFolder={values.selectedFolder}
                  setSelectedFolder={(selectedFolder) => {
                    setFieldValue('selectedFolder', selectedFolder);
                  }}
                  isSubmitting={isSubmitting}
                  url={url}
                  params={params}
                  idToMove={values.idToMove}
                />
                <div
                  className={
                    'Btn BtnOrange DocFormSubmitBtn ' +
                    (isSubmitting ? 'Inactive' : '')
                  }
                  onClick={() => {
                    if (!isSubmitting) submitForm();
                  }}
                >
                  Save
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {formikRef.current?.isSubmitting && <Spinner />}
    </PopupOrPanel>
  );
};

type FileTreeProps = {
  parentFoldersId: number | null;
  selectedFolder: {
    id: number | null;
    name: string | null;
  };
  setSelectedFolder: (selectedFolder: {
    id: number | null;
    name: string | null;
  }) => void;
  isSubmitting: boolean;
  url: string;
  params: Record<string, any>;
  idToMove: number;
};

type TreeResponse = {
  details: {
    folderStructure: Folder[];
  };
};

const FolderTree: React.FC<FileTreeProps> = ({
  parentFoldersId,
  selectedFolder,
  setSelectedFolder,
  isSubmitting,
  url,
  params,
  idToMove,
}) => {
  const [openCreateFolder, setOpenCreateFolder] = useState(false);
  const [updatedFolderTree, setUpdatedFolderTree] = useState<Folder[] | null>(
    null
  );

  const { loading, error, data } = useFetch<TreeResponse>(url, {
    params: { ...params, parentFoldersId },
  });

  // let loading = false;
  // let error = false;
  // let data = testFolderData; // Replace with actual data fetching logic

  const selectFile = (folderId: number | null, folderName: string | null) => {
    if (selectedFolder.id !== folderId) {
      setSelectedFolder({ id: folderId, name: folderName });
    }
  };

  return (
    <div className={'FileTree ' + (isSubmitting ? 'Inactive' : '')}>
      {isSubmitting && <Spinner positionAbsolute />}
      <span className="Label">File Location</span>
      <div className="Tree">
        <button
          className="BtnTree"
          disabled={isSubmitting}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setOpenCreateFolder(true);
          }}
        >
          Create Folder
        </button>
        {loading && !data && !error && <Spinner positionAbsolute />}
        {error && <div className="ErrorTxt">Error loading file tree</div>}
        {data &&
          !error &&
          !updatedFolderTree &&
          data.details.folderStructure &&
          data.details.folderStructure.length > 0 &&
          data.details.folderStructure.map((folder: Folder) => (
            <FolderWithChildren
              key={folder.foldersId}
              folder={folder}
              selectedFolderId={selectedFolder.id}
              selectFile={selectFile}
              level={0}
              idToMove={idToMove}
              isChildOfMovingFolder={
                parentFoldersId !== null && folder.foldersId === parentFoldersId
              }
            />
          ))}
        {updatedFolderTree &&
          updatedFolderTree.length > 0 &&
          updatedFolderTree.map((folder: Folder) => (
            <FolderWithChildren
              key={folder.foldersId}
              folder={folder}
              selectedFolderId={selectedFolder.id}
              selectFile={selectFile}
              level={0}
              idToMove={idToMove}
              isChildOfMovingFolder={
                parentFoldersId !== null && folder.foldersId === parentFoldersId
              }
            />
          ))}
      </div>
      <span className="Label">Selected: {selectedFolder.name}</span>
      {openCreateFolder && (
        <CreateFolder
          panelOpen={openCreateFolder}
          togglePanelOpen={setOpenCreateFolder}
          folderId={selectedFolder.id}
          callback={({ id, name }) => {
            if (data) {
              setUpdatedFolderTree((prev) =>
                prev
                  ? insertNewFolder({ id, name }, selectedFolder.id, prev)
                  : insertNewFolder(
                      { id, name },
                      selectedFolder.id,
                      data.details.folderStructure
                    )
              );
            }
          }}
        />
      )}
      <ErrorM name="genericError" />
    </div>
  );
};

type Folder = {
  foldersId: number | null;
  folderName: string;
  folderStructure: Folder[] | null;
};

type FolderWithChildrenProps = {
  folder: Folder;
  selectedFolderId: number | null;
  selectFile: (folderId: number | null, folderName: string | null) => void;
  level: number;
  idToMove: number;
  isChildOfMovingFolder: boolean;
};

const FolderWithChildren: React.FC<FolderWithChildrenProps> = ({
  folder,
  selectFile,
  selectedFolderId,
  level,
  idToMove,
  isChildOfMovingFolder,
}) => {
  const [expanded, setExpanded] = useState(
    folder.foldersId === selectedFolderId ||
      hasSelectedChild(folder, selectedFolderId)
  );

  const isDescendantOfMovingFolder =
    isChildOfMovingFolder || folder.foldersId === idToMove;

  const isDisabled = isDescendantOfMovingFolder;

  return (
    <>
      <div className="FolderHeader" style={{ marginLeft: `${level * 20}px` }}>
        {folder.folderStructure && folder.folderStructure.length > 0 && (
          <div
            className={
              'ExpandToggle ' +
              (expanded ? '' : 'Collapsed') +
              (isDisabled ? ' Disabled' : '')
            }
            onClick={() => !isDisabled && setExpanded(!expanded)}
          >
            <img src={Chevron} alt="Back to previous page" />
          </div>
        )}
        <div
          className={`FolderName ${
            folder.foldersId === selectedFolderId ? 'Selected' : ''
          } ${isDisabled ? 'Disabled' : ''}`}
          onClick={() =>
            !isDisabled && selectFile(folder.foldersId, folder.folderName)
          }
        >
          {folder.folderName}
        </div>
      </div>

      {expanded &&
        folder.folderStructure &&
        folder.folderStructure.length > 0 && (
          <>
            {folder.folderStructure.map((folder) => (
              <FolderWithChildren
                key={folder.foldersId}
                folder={folder}
                selectedFolderId={selectedFolderId}
                selectFile={selectFile}
                level={level + 1}
                idToMove={idToMove}
                isChildOfMovingFolder={isDescendantOfMovingFolder}
              />
            ))}
          </>
        )}
    </>
  );
};

function hasSelectedChild(folder: Folder, selectedId: number | null): boolean {
  if (!folder.folderStructure || !selectedId) return false;
  return folder.folderStructure.some(
    (child) =>
      child.foldersId === selectedId || hasSelectedChild(child, selectedId)
  );
}

function insertNewFolder(
  newFolder: { id: number | null; name: string },
  selectedFolderId: number | null,
  folders: Folder[]
): Folder[] {
  const newFolderObj: Folder = {
    foldersId: newFolder.id,
    folderName: newFolder.name,
    folderStructure: null,
  };

  if (selectedFolderId === null) {
    return [...folders, newFolderObj];
  } else {
    return updateFolderTree(folders, selectedFolderId, newFolderObj);
  }
}

function updateFolderTree(
  folders: Folder[],
  parentId: number,
  newFolder: Folder
): Folder[] {
  return folders.map((folder) => {
    if (folder.foldersId === parentId) {
      return {
        ...folder,
        folderStructure: folder.folderStructure
          ? [...folder.folderStructure, newFolder]
          : [newFolder],
      };
    }

    if (folder.folderStructure) {
      return {
        ...folder,
        folderStructure: updateFolderTree(
          folder.folderStructure,
          parentId,
          newFolder
        ),
      };
    }

    return folder;
  });
}
